import { validateCreate, hashToken, generateToken, inviteExpiry, holdExpiry, linkUsable } from '../../supabase/functions/_shared/intensiveOffer';

// Deliberately not environment-switchable: enabling production needs separately reviewed code.
export const INTENSIVE_PRODUCTION_ENABLED = false;
export const intensiveSimulationAvailable = () => import.meta.env.DEV &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

export type Interval = { start: string; end: string };
export type Terms = { id: string; body: string; approved: boolean; simulationOnly: boolean };
export type Offer = {
  id: string; instructorId: string; name: string; email: string; phone: string;
  transmission: 'manual' | 'automatic'; hours: number; price: number; deposit: number;
  slots: Interval[]; status: 'link_sent' | 'signed' | 'paid' | 'hold_expired' | 'revoked' | 'payment_problem';
  tokenHash: string; expiresAt: string; holdExpiresAt: string | null; released: boolean;
  terms: Terms | null; acceptance: null | { name: string; signature: string; termsHash: string; acceptedAt: string; earlyStart: boolean };
  orderId: string | null; studentId: string | null;
};
export type PaymentOrder = { id: string; offerId: string; amount: number; currency: 'GBP'; state: 'pending' | 'completed' | 'failed' };
export interface PaymentAdapter {
  readonly mode: 'mock';
  create(offerId: string, amount: number): Promise<PaymentOrder>;
  verify(orderId: string): Promise<PaymentOrder>;
}
export class MockPaymentAdapter implements PaymentAdapter {
  readonly mode = 'mock' as const;
  private orders = new Map<string, PaymentOrder>();
  async create(offerId: string, amount: number) {
    const order: PaymentOrder = { id: crypto.randomUUID(), offerId, amount, currency: 'GBP', state: 'pending' };
    this.orders.set(order.id, order); return { ...order };
  }
  async verify(id: string) {
    const order = this.orders.get(id); if (!order) throw new Error('Unknown simulated payment'); return { ...order };
  }
  complete(id: string, state: 'completed' | 'failed' = 'completed') {
    const order = this.orders.get(id); if (!order) throw new Error('Unknown simulated payment'); order.state = state;
  }
}
const overlaps = (a: Interval, b: Interval) => Date.parse(a.start) < Date.parse(b.end) && Date.parse(a.end) > Date.parse(b.start);
export type SimStudent = { id: string; instructorId: string; email: string; name: string; transmission: string; courseType: 'intensive'; hours: number; status: 'approved' };

/** All records are ephemeral fixtures, never Supabase/auth/calendar writes. One instance = isolated test environment. */
export class IntensiveSimulation {
  private offers = new Map<string, Offer>();
  private students = new Map<string, SimStudent>();
  private tail: Promise<unknown> = Promise.resolve();
  readonly lessons: Array<Interval & { id: string; offerId: string; studentId: string; duration: 120; type: 'intensive' }> = [];
  readonly notifications: Array<{ key: string; kind: string; offerId: string }> = [];
  readonly exceptions: Array<{ offerId: string; orderId: string; reason: string }> = [];
  private sent = new Set<string>();
  constructor(private payment: PaymentAdapter, private now: () => Date, private available: Interval[], private blocked: Interval[] = [], private terms: Terms | null = null) {
    if (payment.mode !== 'mock') throw new Error('Live payments disabled');
  }
  private atomic<T>(work: () => Promise<T>): Promise<T> {
    const result = this.tail.then(work); this.tail = result.catch(() => undefined); return result;
  }
  private notify(offer: Offer, kind: string) {
    const key = `${offer.id}:${kind}`;
    if (!this.sent.has(key)) { this.sent.add(key); this.notifications.push({ key, kind, offerId: offer.id }); }
  }
  expire() {
    for (const offer of this.offers.values()) {
      if (!offer.released && offer.holdExpiresAt && Date.parse(offer.holdExpiresAt) <= this.now().getTime()) {
        offer.released = true;
        if (offer.status !== 'paid' && offer.status !== 'revoked') { offer.status = 'hold_expired'; this.notify(offer, 'hold_expired'); }
      }
    }
  }
  list(instructorId: string) { this.expire(); return [...this.offers.values()].filter(o => o.instructorId === instructorId).map(o => this.safe(o)); }
  private safe(offer: Offer) { const { tokenHash: _token, ...rest } = offer; return structuredClone(rest); }
  private async byToken(token: string) {
    this.expire(); const hash = await hashToken(token);
    const offer = [...this.offers.values()].find(o => o.tokenHash === hash);
    if (!offer) throw new Error('Invalid private link');
    const valid = linkUsable({ status: offer.status, revoked_at: offer.status === 'revoked' ? this.now().toISOString() : null, link_expires_at: offer.expiresAt }, this.now());
    if (valid.ok === false) throw new Error(`Private link ${valid.reason}`); return offer;
  }
  private checkSlots(slots: Interval[], exclude?: string) {
    this.expire();
    for (const slot of slots) {
      if (!Number.isFinite(Date.parse(slot.start)) || Date.parse(slot.end) - Date.parse(slot.start) !== 7200000 || Date.parse(slot.start) <= this.now().getTime()) throw new Error('Choose future two-hour lesson slots');
      if (!this.available.some(a => Date.parse(a.start) <= Date.parse(slot.start) && Date.parse(a.end) >= Date.parse(slot.end))) throw new Error('Outside instructor availability');
      if (this.blocked.some(b => overlaps(b, slot))) throw new Error('Slot overlaps a break or blocked time');
      if (this.lessons.some(l => overlaps(l, slot))) throw new Error('Slot already booked');
      if ([...this.offers.values()].some(o => o.id !== exclude && !o.released && o.slots.some(s => overlaps(s, slot)))) throw new Error('Slot held by another offer');
      if (slots.some(s => s !== slot && overlaps(s, slot))) throw new Error('Selected slots overlap');
    }
  }
  async create(instructorId: string, input: { name: string; email: string; phone: string; hours: number; transmission: 'manual' | 'automatic'; price: number; deposit?: number; slots?: Interval[] }) {
    return this.atomic(async () => {
      if (!instructorId) throw new Error('Instructor authorization required');
      const v = validateCreate({ courseHours: input.hours, transmission: input.transmission, totalPricePence: input.price, depositPence: input.deposit, learnerEmail: input.email });
      if (v.ok === false) throw new Error(v.error);
      const slots = input.slots ?? [];
      if (slots.length * 2 > input.hours) throw new Error('Selected lessons exceed course hours');
      this.checkSlots(slots);
      const token = generateToken(); const now = this.now();
      const offer: Offer = { id: crypto.randomUUID(), instructorId, name: input.name.trim(), email: input.email.trim().toLowerCase(), phone: input.phone.trim(), hours: input.hours, transmission: input.transmission, price: input.price, deposit: v.value.depositPence, slots: structuredClone(slots), status: 'link_sent', tokenHash: await hashToken(token), expiresAt: inviteExpiry(now).toISOString(), holdExpiresAt: slots.length ? holdExpiry(now).toISOString() : null, released: slots.length === 0, terms: this.terms ? structuredClone(this.terms) : null, acceptance: null, orderId: null, studentId: null };
      this.offers.set(offer.id, offer); this.notify(offer, 'invitation'); return { offer: this.safe(offer), token };
    });
  }
  async review(token: string) { return this.safe(await this.byToken(token)); }
  async manage(instructorId: string, id: string, action: 'revoke' | 'resend' | 'extend' | 'rehold') {
    return this.atomic(async () => {
      this.expire(); const offer = this.offers.get(id);
      if (!instructorId || offer?.instructorId !== instructorId) throw new Error('Offer not found');
      if (offer.status === 'paid' || offer.status === 'revoked' || offer.orderId) throw new Error('Offer locked; pending payment requires review');
      let token: string | undefined;
      if (action === 'revoke') { offer.status = 'revoked'; offer.released = true; this.notify(offer, 'revoked'); }
      if (action === 'resend') { token = generateToken(); offer.tokenHash = await hashToken(token); offer.expiresAt = inviteExpiry(this.now()).toISOString(); }
      if (action === 'extend') offer.expiresAt = inviteExpiry(new Date(Math.max(this.now().getTime(), Date.parse(offer.expiresAt)))).toISOString();
      if (action === 'rehold') {
        if (Date.parse(offer.expiresAt) <= this.now().getTime()) throw new Error('Invitation expired');
        if (!offer.slots.length) throw new Error('Dates are to be arranged');
        this.checkSlots(offer.slots, id); offer.holdExpiresAt = holdExpiry(this.now()).toISOString(); offer.released = false; offer.status = offer.acceptance ? 'signed' : 'link_sent'; this.notify(offer, 'dates_rechecked');
      }
      return { offer: this.safe(offer), token };
    });
  }
  async sign(token: string, name: string, signature: string, consent: boolean, earlyStart: boolean, details: { name: string; email: string; phone: string }) {
    return this.atomic(async () => {
      const offer = await this.byToken(token);
      if (!offer.terms?.approved) throw new Error('Terms awaiting Ian’s approval');
      if (offer.acceptance) throw new Error('Acceptance already recorded');
      if (!consent || !name.trim() || !signature.startsWith('data:image/png;base64,') || signature.length < 50 || signature.length > 250000) throw new Error('Terms consent and electronic signature required');
      if (!details.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email) || !details.phone.trim()) throw new Error('Learner name, email and mobile required');
      if (offer.email && offer.email !== details.email.trim().toLowerCase()) throw new Error('Contact Ian to change the invited email');
      if (offer.slots.some(s => Date.parse(s.start) < this.now().getTime() + 14 * 86400000) && !earlyStart) throw new Error('Express early-start request required for dates within 14 days');
      offer.name = details.name.trim(); offer.email = details.email.trim().toLowerCase(); offer.phone = details.phone.trim();
      offer.acceptance = { name: name.trim(), signature, termsHash: await hashToken(offer.terms.body), acceptedAt: this.now().toISOString(), earlyStart };
      offer.status = 'signed'; return this.safe(offer);
    });
  }
  private payable(offer: Offer) {
    if (!offer.acceptance || !offer.terms?.approved) throw new Error('Approved terms and signature required');
    if (offer.slots.length && offer.released) throw new Error('Held dates released; Ian must recheck before payment');
    this.checkSlots(offer.slots, offer.id);
    const student = this.students.get(offer.email);
    if (student && student.instructorId !== offer.instructorId) throw new Error('Existing learner belongs to another instructor');
    if (student && student.hours > 0 && student.id !== offer.studentId) throw new Error('Existing intensive allocation requires Ian’s review');
  }
  async checkout(token: string) {
    return this.atomic(async () => {
      const offer = await this.byToken(token); this.payable(offer);
      if (offer.orderId) return this.payment.verify(offer.orderId);
      const order = await this.payment.create(offer.id, offer.deposit); offer.orderId = order.id; return order;
    });
  }
  /** Event is a wake-up only. Adapter verification is the source of truth, not the event payload. */
  async confirm(orderId: string) {
    return this.atomic(async () => {
      this.expire(); const order = await this.payment.verify(orderId);
      const offer = [...this.offers.values()].find(o => o.orderId === orderId);
      if (!offer || order.offerId !== offer.id || order.amount !== offer.deposit || order.currency !== 'GBP') throw new Error('Payment identity or amount mismatch');
      if (offer.status === 'paid') return { status: 'paid', studentId: offer.studentId, alreadyConfirmed: true };
      if (order.state !== 'completed') return { status: order.state, studentId: null, alreadyConfirmed: false };
      try {
        const valid = linkUsable({ status: offer.status, revoked_at: offer.status === 'revoked' ? this.now().toISOString() : null, link_expires_at: offer.expiresAt }, this.now());
        if (!valid.ok) throw new Error('Offer no longer valid');
        this.payable(offer);
      } catch (error) {
        offer.status = 'payment_problem';
        if (!this.exceptions.some(e => e.orderId === orderId)) this.exceptions.push({ offerId: offer.id, orderId, reason: error instanceof Error ? error.message : 'Availability changed' });
        this.notify(offer, 'payment_problem'); return { status: 'payment_problem', studentId: null, alreadyConfirmed: false };
      }
      const student: SimStudent = this.students.get(offer.email) ?? { id: crypto.randomUUID(), email: offer.email, name: offer.name, instructorId: offer.instructorId, transmission: offer.transmission, courseType: 'intensive', hours: offer.hours, status: 'approved' };
      this.students.set(offer.email, student); offer.studentId = student.id;
      for (const slot of offer.slots) this.lessons.push({ ...slot, id: crypto.randomUUID(), offerId: offer.id, studentId: student.id, duration: 120, type: 'intensive' });
      offer.released = true; offer.status = 'paid'; this.notify(offer, 'learner_confirmation'); this.notify(offer, 'instructor_confirmation'); this.notify(offer, 'calendar_sync');
      return { status: 'paid', studentId: student.id, alreadyConfirmed: false };
    });
  }
  studentList() { return structuredClone([...this.students.values()]); }
  // Test fixture injection only; no live records can be read or changed.
  addBlocked(slot: Interval) { this.blocked.push(structuredClone(slot)); }
  seedStudent(student: SimStudent) { this.students.set(student.email.toLowerCase(), structuredClone(student)); }
}